import React, { useEffect, useState } from 'react';
import { Ticket, Plus, Copy, Check, Ban, RotateCcw } from 'lucide-react';
import { api } from '../../App';
import toast from 'react-hot-toast';
import ConfirmationDialog from '../ConfirmationDialog';
import { formatDate } from '../../utils/dateUtils';

interface SignupCode {
  id: string;
  code: string;
  effectiveStartDate: string | null;
  effectiveEndDate: string | null;
  isActive: boolean;
  status: 'active' | 'scheduled' | 'expired' | 'deactivated';
  signupCount: number;
  createdAt: string;
}

interface SignupCodesCardProps {
  organizationId: string;
}

const STATUS_STYLES: Record<SignupCode['status'], string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  expired: 'bg-background-tertiary text-neutral-700 dark:bg-background-dark-secondary dark:text-text-dark-secondary',
  deactivated: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const joinLinkFor = (code: string) => `${window.location.origin}/join/${code}`;

const SignupCodesCard: React.FC<SignupCodesCardProps> = ({ organizationId }) => {
  const [codes, setCodes] = useState<SignupCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCode, setNewCode] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<SignupCode | null>(null);

  const fetchCodes = async () => {
    try {
      const res = await api.get(`/organizations/${organizationId}/signup-codes`);
      if (res.data?.success) setCodes(res.data.data);
    } catch (err) {
      console.error('Failed to fetch sign-up codes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organizationId) fetchCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCode.trim()) return;
    setCreating(true);
    try {
      const res = await api.post(`/organizations/${organizationId}/signup-codes`, {
        code: newCode.trim(),
        effectiveStartDate: startDate || undefined,
        effectiveEndDate: endDate || undefined,
      });
      if (res.data?.success) {
        setCodes(prev => [res.data.data, ...prev]);
        setNewCode('');
        setStartDate('');
        setEndDate('');
        toast.success('Sign-up code created');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (codeRow: SignupCode) => {
    try {
      const res = await api.patch(
        `/organizations/${organizationId}/signup-codes/${codeRow.id}`,
        { isActive: !codeRow.isActive },
      );
      if (res.data?.success) {
        setCodes(prev => prev.map(c => (c.id === codeRow.id ? { ...c, ...res.data.data } : c)));
        toast.success(codeRow.isActive ? 'Code deactivated' : 'Code reactivated');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to update code');
    } finally {
      setDeactivateTarget(null);
    }
  };

  const handleCopy = async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(joinLinkFor(code));
      setCopiedId(id);
      toast.success('Link copied');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  return (
    <div className="card">
      <div className="flex items-center space-x-2 mb-1">
        <Ticket className="h-5 w-5 text-primary-700 dark:text-primary-400" />
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">Sign-up codes</h3>
      </div>
      <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
        Create codes to invite members to join {' '}as citizens. Share each code's link; anyone who signs
        up through it is affiliated with your organization.
      </p>

      {/* Create form */}
      <form onSubmit={handleCreate} className="flex flex-wrap items-end gap-3 mb-6">
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">Code</label>
          <input
            type="text"
            value={newCode}
            onChange={e => setNewCode(e.target.value.toUpperCase())}
            placeholder="e.g. TEACHERS2026"
            maxLength={32}
            className="w-full px-3 py-2 rounded-md border border-border-light dark:border-border-dark bg-white dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">Starts (optional)</label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="px-3 py-2 rounded-md border border-border-light dark:border-border-dark bg-white dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">Ends (optional)</label>
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="px-3 py-2 rounded-md border border-border-light dark:border-border-dark bg-white dark:bg-background-dark-tertiary text-text-primary dark:text-text-dark-primary text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={creating || !newCode.trim()}
          className="flex items-center gap-1.5 px-4 py-2 bg-primary-600 dark:bg-primary-700 text-white text-sm font-medium rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="w-4 h-4" />
          {creating ? 'Creating…' : 'Create'}
        </button>
      </form>

      {/* Codes list */}
      {loading ? (
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">Loading…</p>
      ) : codes.length === 0 ? (
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">No sign-up codes yet.</p>
      ) : (
        <div className="space-y-3">
          {codes.map(codeRow => (
            <div
              key={codeRow.id}
              className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-md border border-border-light dark:border-border-dark"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-text-primary dark:text-text-dark-primary">{codeRow.code}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_STYLES[codeRow.status]}`}>
                    {codeRow.status}
                  </span>
                </div>
                <div className="text-xs text-text-secondary dark:text-text-dark-secondary mt-1">
                  {formatDate(codeRow.effectiveStartDate)} – {formatDate(codeRow.effectiveEndDate)}
                  {' · '}
                  {codeRow.signupCount} {codeRow.signupCount === 1 ? 'signup' : 'signups'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleCopy(codeRow.code, codeRow.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border border-border-light dark:border-border-dark text-text-primary dark:text-text-dark-primary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                  title="Copy sign-up link"
                >
                  {copiedId === codeRow.id ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  Copy link
                </button>
                {codeRow.isActive ? (
                  <button
                    onClick={() => setDeactivateTarget(codeRow)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Deactivate code"
                  >
                    <Ban className="w-4 h-4" />
                    Deactivate
                  </button>
                ) : (
                  <button
                    onClick={() => handleToggleActive(codeRow)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary transition-colors"
                    title="Reactivate code"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reactivate
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmationDialog
        isOpen={!!deactivateTarget}
        title="Deactivate sign-up code"
        message={`Deactivate ${deactivateTarget?.code}? Its link will stop working immediately. Members who already joined keep their affiliation, and you can reactivate it later.`}
        confirmText="Deactivate"
        cancelText="Cancel"
        variant="danger"
        onConfirm={() => deactivateTarget && handleToggleActive(deactivateTarget)}
        onCancel={() => setDeactivateTarget(null)}
      />
    </div>
  );
};

export default SignupCodesCard;
